// /refills — manual refill tracking. User sets the last fill date
// and days supply per medication; we compute the next refill due
// date and surface medicines running low.

import type { Medication } from '@medreview/shared';
import { useEffect, useState } from 'react';
import { listMedications } from '../api/medications';
import { AmazonReorderLink } from '../components/AmazonReorderLink';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { AppPage } from '../layouts/AppPage';

interface DueItem {
  id: string;
  name: string;
  lastRefillDate: string | null;
  daysSupply: number | null;
  nextRefillDue: string | null;
  daysLeft: number | null;
}

export function Refills(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const [meds, setMeds] = useState<Medication[]>([]);
  const [due, setDue] = useState<DueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const reload = async () => {
    const [medList, dueRes] = await Promise.all([
      listMedications(authedFetch),
      authedFetch('/api/refills/due'),
    ]);
    setMeds(medList);
    const json = (await dueRes.json()) as { ok: boolean; data?: { items: DueItem[] } };
    if (json.ok && json.data) setDue(json.data.items);
    setLoading(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only fetch
  useEffect(() => {
    void reload();
  }, []);

  const save = async (medicationId: string, lastRefillDate: string, daysSupply: number) => {
    setSaving(medicationId);
    try {
      await authedFetch(`/api/refills/${medicationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ lastRefillDate, daysSupply }),
      });
      await reload();
    } finally {
      setSaving(null);
    }
  };

  return (
    <AppPage title="Refills" back={{ to: '/medications', label: 'Medicines' }}>
      {loading ? <p style={{ padding: 'var(--space-md)' }}>Loading...</p> : null}

      {!loading && due.length > 0 ? (
        <section className="account-section">
          <h3 className="account-section__title">Running low</h3>
          <ul className="food-list">
            {due.map((d) => {
              const tone =
                d.daysLeft === null
                  ? 'food-pill--note'
                  : d.daysLeft <= 0
                    ? 'food-pill--avoid'
                    : d.daysLeft <= 7
                      ? 'food-pill--limit'
                      : 'food-pill--note';
              const label =
                d.daysLeft === null
                  ? 'No info'
                  : d.daysLeft <= 0
                    ? 'Overdue'
                    : `${d.daysLeft} day${d.daysLeft === 1 ? '' : 's'} left`;
              return (
                <li key={d.id} className="food-item">
                  <span className={`food-pill ${tone}`}>{label}</span>
                  <div>
                    <p className="food-item__name">{d.name}</p>
                    <p className="food-item__reason">
                      Last filled: {d.lastRefillDate ?? 'unknown'}. You have{' '}
                      {d.daysSupply ?? 'unknown'} day{d.daysSupply === 1 ? '' : 's'} left in the
                      bottle. Pick up by: {d.nextRefillDue ?? 'unknown'}.
                    </p>
                    <AmazonReorderLink name={d.name} />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <section className="account-section">
        <h3 className="account-section__title">Set your refill info</h3>
        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
          For each medicine you want to track, enter the date you last picked it up and how many
          days it should last. We will warn you when it is time to reorder.
        </p>
        <ul className="med-list">
          {meds.map((m) => (
            <li key={m.id}>
              <RefillForm
                med={m}
                onSave={(date, days) => save(m.id, date, days)}
                disabled={saving === m.id}
              />
            </li>
          ))}
        </ul>
      </section>
    </AppPage>
  );
}

function RefillForm({
  med,
  onSave,
  disabled,
}: {
  med: Medication;
  onSave: (date: string, days: number) => void;
  disabled: boolean;
}): JSX.Element {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [days, setDays] = useState('30');
  return (
    <article className="refill-form">
      <p className="med-card__title" style={{ fontSize: 18 }}>
        {med.name}
        {med.strength ? ` (${med.strength})` : ''}
      </p>
      <div className="refill-form__row">
        <label>
          Last fill date
          <input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label>
          How many days' worth in the bottle
          <input
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="ghost-cta"
          onClick={() => onSave(date, Number(days))}
          disabled={disabled || Number(days) < 1}
        >
          {disabled ? 'Saving...' : 'Save'}
        </button>
      </div>
    </article>
  );
}
